import {
    EventrDate,
    getObjectById,
    getTimezoneNumberFromFlightField,
    IEventrFlight,
    IField,
    updateFlightData,
} from '@eventr/common'
import {
    drawerStore,
    MainButton,
    MobileDrawerFrame,
} from '@eventr/common-storybook'
import { Box } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { DataAPI } from '../../../data/Data'
import i18n from '../../../i18n/i18next'
import { useBoundStore } from '../../../state/bound.store'
import { defaultAddSnack } from '../../../state/overlayState/overlayContext'
import { loadingSignal } from '../../../state/overlayState/overlaySignals'
import { reactAppStage } from '../../../utils/reactAppStage'
import { FlightAirports } from '../../../v2/componentCards/flightComponents/flightAirports'
import { FlightDate } from '../../../v2/componentCards/flightComponents/flightDate'
import { FlightTimes } from '../../../v2/componentCards/flightComponents/flightTimes'
import { BaseCard } from '../../../v2/componentCards/subComponents/baseCard'
import { useEditSectionContext } from '../editSectionProvider'

interface IGetFlightFieldProps {
    field: IField
    _id?: string
}

export const GetFlightData = ({ _id }: IGetFlightFieldProps) => {
    const { updateValues, values } = useEditSectionContext()
    const [state, setState] = useState<any>()
    const { org, selectedItinID } = useBoundStore(
        useShallow((s) => {
            return { org: s.selectedOrg, selectedItinID: s.selectedItinID }
        })
    )

    function updateFlightTime(
        key: any,
        flight: IEventrFlight,
        value: any
    ): string {
        const timezoneNumber = getTimezoneNumberFromFlightField(key, flight)
        const newEventrDate = new EventrDate(value.replace(' ', 'T'))
        newEventrDate.setTimezoneEnum(timezoneNumber, false)
        return newEventrDate.dateTimeString
    }
    useEffect(() => {
        if (_id) {
            const obj = getObjectById(_id, values)
            setState(obj)
        }
    }, [_id, values])
    const handleClick = async () => {
        loadingSignal.value = true

        const date = new EventrDate(state.flightDate).toItinLocalDateString()

        const resp = await DataAPI.getFlightData(state.bookref, date, {
            itineraryId: selectedItinID,
            orgId: org?.name ?? '',
            flightId: _id ?? '',
            env: reactAppStage(),
        })

        if (resp.status === 'error') {
            loadingSignal.value = false
            defaultAddSnack(
                i18n.t('edit-itinerary.fields.cant-find-flight'),
                'error'
            )
        } else {
            loadingSignal.value = false

            if (resp.data.length === 0) {
                defaultAddSnack(
                    i18n.t('edit-itinerary.fields.cant-find-flight'),
                    'error'
                )
                return
            }
            if (resp.data.length === 1) {
                const newFlight = resp.data[0]

                updateFlightData(state, newFlight)
                updateValues({ _id, value: state })
            } else {
                drawerStore().openDrawer({
                    node: (
                        <MobileDrawerFrame
                            title={i18n.t('drawer.flights.header')}
                            minHeight="30vh"
                        >
                            <Box
                                display={'grid'}
                                gridTemplateRows={'auto auto 1fr auto'}
                                width={'100%'}
                            >
                                {resp.data.map(
                                    (flight: IEventrFlight, index: number) => (
                                        <BaseCard
                                            past={false}
                                            key={index}
                                            onClick={() => {
                                                const newFlight =
                                                    resp.data[index]
                                                updateFlightData(
                                                    state,
                                                    newFlight
                                                )
                                                updateValues({
                                                    _id,
                                                    value: state,
                                                })
                                                drawerStore().closeDrawer()
                                            }}
                                        >
                                            <FlightDate
                                                date={flight.flightDate}
                                            />
                                            <FlightAirports
                                                origin={
                                                    flight.departure?.dep_name
                                                }
                                                destination={
                                                    flight.arrival?.arr_name
                                                }
                                                originTerminal={
                                                    flight.departure
                                                        ?.dep_terminal
                                                }
                                                destinationTerminal={
                                                    flight.arrival?.arr_terminal
                                                }
                                            />
                                            <FlightTimes
                                                originAirportABR={
                                                    flight.outboundAirportAbr
                                                }
                                                destinationAirportABR={
                                                    flight.inboundAirportAbr
                                                }
                                                destinationTime={updateFlightTime(
                                                    'arr_time',
                                                    flight,
                                                    flight.arrival?.arr_time
                                                )}
                                                originTime={updateFlightTime(
                                                    'dep_time',
                                                    flight,
                                                    flight.departure?.dep_time
                                                )}
                                            />
                                        </BaseCard>
                                    )
                                )}
                                <Box height={'16px'} />

                                <MainButton
                                    variant="secondary"
                                    text={i18n.t('general.cancel')}
                                    onClick={() => {
                                        drawerStore().closeDrawer()
                                    }}
                                />
                            </Box>
                        </MobileDrawerFrame>
                    ),
                    onBackdropClick: () => drawerStore().closeDrawer(),
                })
            }
        }
        loadingSignal.value = false
    }

    const isDisabled = useMemo(() => {
        return state?.bookref && state?.flightDate ? false : true
    }, [state?.bookref, state?.flightDate])

    return (
        <MainButton
            disabled={isDisabled}
            onClick={handleClick}
            id="getFlightData"
        >
            {i18n.t('edit-itinerary.fields.get-flight-data')}
        </MainButton>
    )
}
